import { useEffect, useState } from 'react';
import { legalPackIdsForArmor, resolveLegalLoadout, ARMOR_CLASSES } from '../shared/game';
import type { ArmorClass, PackId } from '../shared/game';

export function resolveArmorStatPreview(armorClass: string): {
  speedRating: number;
  healthRating: number;
  jetRating: number;
  armorRating: number;
} {
  if (armorClass === 'light') {
    return { speedRating: 5, healthRating: 2, jetRating: 5, armorRating: 1 };
  }
  if (armorClass === 'heavy') {
    return { speedRating: 2, healthRating: 5, jetRating: 2, armorRating: 5 };
  }
  return { speedRating: 3, healthRating: 3, jetRating: 3, armorRating: 3 };
}

function StatBar({ label, value, max = 5 }: { label: string; value: number; max?: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
      <span style={{ color: '#8899aa', fontSize: 11, width: 56, textTransform: 'uppercase' }}>{label}</span>
      <div style={{ display: 'flex', gap: 3 }}>
        {Array.from({ length: max }, (_, i) => (
          <div
            key={i}
            style={{
              width: 14,
              height: 8,
              background: i < value ? '#4af' : '#223',
              borderRadius: 2,
            }}
          />
        ))}
      </div>
    </div>
  );
}

interface RespawnScreenProps {
  respawnCountdown: number;
  currentArmorClass: ArmorClass;
  onConfirm: (armorClass: ArmorClass, weaponId: string, packId: string) => void;
}

const respawnKeyframes = `
  @keyframes redFlash {
    from { opacity: 0.5; }
    to { opacity: 0; }
  }
  @keyframes countdownPulse {
    from { transform: scale(1.5); opacity: 0.4; }
    to { transform: scale(1); opacity: 1; }
  }
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(16px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const armorColors: Record<ArmorClass, string> = {
  light: '#00eeff',
  medium: '#ffcc44',
  heavy: '#ff4422',
};

const armorOptions: { id: ArmorClass; label: string; subtitle: string }[] = [
  { id: 'light', label: 'PATHFINDER', subtitle: 'Fast. Nimble. Flag runner.' },
  { id: 'medium', label: 'RANGER', subtitle: 'Balanced. Versatile.' },
  { id: 'heavy', label: 'JUGGERNAUT', subtitle: 'Slow. Durable. Fire power.' },
];

// Phase 121: Pack picker — short labels for the loadout chooser.
const packLabels: Record<PackId, string> = {
  energy: 'ENERGY',
  shield: 'SHIELD',
  repair: 'REPAIR',
  sensor: 'SENSOR',
  jammer: 'JAMMER',
  ammo: 'AMMO',
  cloak: 'CLOAK',
};

export function RespawnScreen({ respawnCountdown, currentArmorClass, onConfirm }: RespawnScreenProps) {
  const [selectedArmor, setSelectedArmor] = useState<ArmorClass>(currentArmorClass);
  const legalPacks = legalPackIdsForArmor(selectedArmor);
  const [selectedPack, setSelectedPack] = useState<PackId>(legalPacks[0] ?? 'energy');
  // Phase 121: Keep pack selection legal whenever armor changes.
  useEffect(() => {
    const allowed = legalPackIdsForArmor(selectedArmor);
    if (!allowed.includes(selectedPack)) {
      setSelectedPack(allowed[0] ?? 'energy');
    }
  }, [selectedArmor, selectedPack]);

  const stats = resolveArmorStatPreview(selectedArmor);
  const legalLoadout = resolveLegalLoadout(selectedArmor, null, selectedPack);

  const countdownNum = Math.ceil(respawnCountdown);
  const ratio = Math.min(Math.max(countdownNum, 0) / 5, 1);
  const cr = Math.round(255 * ratio);
  const cg = Math.round(200 * (1 - ratio));
  const countdownColor = `rgb(${cr}, ${cg}, 60)`;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.82)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        fontFamily: 'monospace',
      }}
    >
      <style>{respawnKeyframes}</style>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(255, 40, 40, 0.4)',
          animation: 'redFlash 0.6s ease-out forwards',
          pointerEvents: 'none',
        }}
      />
      <div
        key={countdownNum}
        style={{
          fontSize: 56,
          fontWeight: 800,
          color: countdownColor,
          letterSpacing: '0.1em',
          animation: 'countdownPulse 0.5s ease-out',
          lineHeight: 1,
          marginBottom: 8,
        }}
      >
        {countdownNum}
      </div>
      <div style={{ fontSize: 11, color: '#8899aa', letterSpacing: '0.2em', marginBottom: 28, textTransform: 'uppercase' }}>
        Respawn in
      </div>
      <div style={{ color: '#fff', fontSize: 20, letterSpacing: '0.3em', marginBottom: 28, animation: 'fadeInUp 0.4s ease-out both' }}>
        SELECT LOADOUT
      </div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 28 }}>
        {armorOptions.map((opt, index) => {
          const optStats = resolveArmorStatPreview(opt.id);
          const isSelected = selectedArmor === opt.id;
          return (
            <button
              key={opt.id}
              onClick={() => setSelectedArmor(opt.id)}
              onPointerEnter={(e) => {
                e.currentTarget.style.boxShadow = `0 0 24px ${armorColors[opt.id]}50`;
                e.currentTarget.style.transform = 'translateY(-3px)';
              }}
              onPointerLeave={(e) => {
                e.currentTarget.style.boxShadow = isSelected ? `0 0 16px ${armorColors[opt.id]}40` : 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
              style={{
                width: 160,
                padding: '16px 12px',
                background: isSelected ? 'rgba(30,40,60,0.9)' : 'rgba(10,14,22,0.7)',
                border: `2px solid ${isSelected ? armorColors[opt.id] : '#334'}`,
                borderRadius: 8,
                cursor: 'pointer',
                color: isSelected ? armorColors[opt.id] : '#667',
                textAlign: 'center',
                transition: 'all 0.25s ease',
                boxShadow: isSelected ? `0 0 16px ${armorColors[opt.id]}40` : 'none',
                animation: `fadeInUp 0.4s ease-out ${index * 0.1}s both`,
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 'bold',
                  letterSpacing: '0.15em',
                  marginBottom: 6,
                }}
              >
                {opt.label}
              </div>
              <div style={{ fontSize: 10, color: '#556', marginBottom: 12 }}>
                {opt.subtitle}
              </div>
              <StatBar label="Speed" value={optStats.speedRating} />
              <StatBar label="Health" value={optStats.healthRating} />
              <StatBar label="Jet" value={optStats.jetRating} />
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: '#667', marginBottom: 12 }}>
        {stats.armorRating >= 4 ? 'High armor rating' : stats.armorRating <= 2 ? 'Low armor rating' : 'Medium armor rating'}{' '}
        {'\u00b7'}{' '}
        {legalLoadout.weaponId.replace(/_/g, ' ').toUpperCase()} default
      </div>
      {/* Phase 121: Pack picker \u2014 single row of pill buttons for legal packs. */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 22, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 540 }}>
        {legalPacks.map((pid) => {
          const active = selectedPack === pid;
          return (
            <button
              key={pid}
              onClick={() => setSelectedPack(pid)}
              style={{
                padding: '6px 12px',
                background: active ? armorColors[selectedArmor] : 'rgba(10,14,22,0.7)',
                color: active ? '#000' : '#aab',
                border: `1px solid ${active ? armorColors[selectedArmor] : '#334'}`,
                borderRadius: 4,
                cursor: 'pointer',
                fontFamily: 'monospace',
                fontSize: 10,
                letterSpacing: 1.4,
                fontWeight: 700,
                transition: 'all 0.15s ease',
              }}
            >
              {packLabels[pid] ?? pid.toUpperCase()}
            </button>
          );
        })}
      </div>
      <button
        onClick={() => onConfirm(selectedArmor, legalLoadout.weaponId, legalLoadout.packId)}
        onPointerEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.04)';
          e.currentTarget.style.boxShadow = `0 0 24px ${armorColors[selectedArmor]}60`;
        }}
        onPointerLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = 'none';
        }}
        style={{
          padding: '12px 48px',
          background: armorColors[selectedArmor],
          color: '#000',
          border: 'none',
          borderRadius: 6,
          cursor: 'pointer',
          fontSize: 14,
          fontWeight: 'bold',
          letterSpacing: '0.2em',
          fontFamily: 'monospace',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        }}
      >
        CONFIRM
      </button>
      <div style={{ marginTop: 16, fontSize: 10, color: '#446' }}>
        {respawnCountdown <= 0 ? 'SPAWNING...' : 'ENTER to confirm'}
      </div>
    </div>
  );
}

export { ARMOR_CLASSES };
